import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { PageHeader, StatusPill, EmptyState } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, Check, X, HelpCircle, ArrowRight } from "lucide-react";

export default function ServiceRequests() {
  const { version, refresh } = useAdmin();
  const [reqs, setReqs] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/service-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [version]);

  const act = async (r, status) => {
    try {
      const res = await api.post(`/service-requests/${r.request_id}/status`, { status });
      if (status === "approved") {
        toast.success("Approved — client ready. Add a service & send invitation.");
        refresh();
        if (res.data.client_id) { navigate(`/admin/clients/${res.data.client_id}`); return; }
      } else {
        toast.success(`Request ${status.replace("_", " ")}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const pending = reqs.filter((r) => r.status === "pending").length;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Service Requests" subtitle={`${pending} pending review`} />
      {reqs.length === 0 ? (
        <EmptyState icon={Inbox} title="No service requests" hint="Requests submitted from the public website appear here." />
      ) : (
        <div className="space-y-3" data-testid="service-requests-list">
          {reqs.map((r) => (
            <div key={r.request_id} data-testid={`sr-row-${r.request_id}`} className="bg-white border border-[#E2EBF0] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#0D3B2D]">{r.name}</span>
                    <StatusPill status={r.status === "info_requested" ? "pending" : r.status} />
                  </div>
                  <div className="text-xs text-[#4A635A] mt-0.5">{r.email}{r.company ? ` · ${r.company}` : ""}{r.phone ? ` · ${r.phone}` : ""}</div>
                  <div className="text-sm text-[#0D3B2D] mt-2"><span className="font-medium">Requested:</span> {r.service_requested}</div>
                  {r.details && <p className="text-sm text-[#4A635A] mt-1">{r.details}</p>}
                  <div className="text-[11px] text-[#4A635A]/70 font-mono mt-2">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                {r.status === "pending" && (
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => act(r, "approved")} size="sm" data-testid={`sr-approve-${r.request_id}`} className="bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg text-xs">
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                    <Button onClick={() => act(r, "info_requested")} size="sm" variant="outline" data-testid={`sr-info-${r.request_id}`} className="border-[#E2EBF0] text-[#4A635A] rounded-lg text-xs">
                      <HelpCircle className="h-3.5 w-3.5 mr-1" /> Request Info
                    </Button>
                    <Button onClick={() => act(r, "rejected")} size="sm" variant="outline" data-testid={`sr-reject-${r.request_id}`} className="border-red-200 text-red-600 rounded-lg text-xs">
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
                {r.status === "approved" && r.client_id && (
                  <Button onClick={() => navigate(`/admin/clients/${r.client_id}`)} size="sm" variant="outline" data-testid={`sr-goto-${r.request_id}`} className="border-[#BCECD8] text-[#10B981] rounded-lg text-xs">
                    Open Client <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
